/**
 * Collapsible previous-residence list for the Rental Status card.
 * Header: month-year range, rent, duration, chevron. Body: landlord /
 * address / dates from the rental step plus Minimum Living History extras.
 */
import { useState } from 'react'
import {
	formatMoneyValue,
	optionLabel,
} from '#app/utils/application-detail-v2/display'
import type { PreviousResidenceRow } from '#app/utils/application-detail-v2/rental-history-display'
import { cn } from '#app/utils/misc'
import { formatPhoneNumberWithFallback } from '#app/utils/phone-formatting'
import { FieldBox } from '../primitives'

export function PreviousRentalList({
	items,
	showLandlordPhone,
	showLandlordEmail,
	formatDate,
}: {
	items: PreviousResidenceRow[]
	showLandlordPhone: (value: string | undefined) => boolean
	showLandlordEmail: (value: string | undefined) => boolean
	formatDate: (value: string) => string
}) {
	const firstId = items[0]?.id ?? null
	const [openId, setOpenId] = useState<string | null>(firstId)
	const [leadId, setLeadId] = useState(firstId)
	// Keep the first row open by default when the lead residence changes
	// (e.g. switching applicants on the combined report).
	if (firstId !== leadId) {
		setLeadId(firstId)
		setOpenId(firstId)
	}
	if (items.length === 0) return null

	return (
		<div className="space-y-3 xl:col-span-2">
			{items.map((item) => {
				const open = item.id === openId
				return (
					<ResidenceAccordion
						key={item.id}
						item={item}
						open={open}
						onToggle={() => setOpenId(open ? null : item.id)}
						showLandlordPhone={showLandlordPhone}
						showLandlordEmail={showLandlordEmail}
						formatDate={formatDate}
					/>
				)
			})}
		</div>
	)
}

function ResidenceAccordion({
	item,
	open,
	onToggle,
	showLandlordPhone,
	showLandlordEmail,
	formatDate,
}: {
	item: PreviousResidenceRow
	open: boolean
	onToggle: () => void
	showLandlordPhone: (value: string | undefined) => boolean
	showLandlordEmail: (value: string | undefined) => boolean
	formatDate: (value: string) => string
}) {
	const range = item.rangeLabel || '-'
	const duration = item.durationLabel || '-'
	const rent = formatMoneyValue(item.rent || undefined)
	const headerDetail = item.showLandlordFields
		? rent
		: optionLabel(item.livingSituation) || '-'
	const start = item.moveInIso ? formatDate(item.moveInIso) : ''
	const end = item.moveOutIso ? formatDate(item.moveOutIso) : ''
	const panelId = `${item.id}-panel`

	return (
		<div className="rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD]">
			<button
				type="button"
				aria-expanded={open}
				aria-controls={panelId}
				className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left"
				onClick={onToggle}
			>
				<span className="min-w-0 flex-1">
					<span className="block text-[15px] font-semibold text-[#32302F]">
						{range}
					</span>
					<span className="block text-[13px] text-[#747474]">
						{headerDetail}
					</span>
				</span>
				<span className="shrink-0 text-[13px] text-[#747474]">{duration}</span>
				<img
					src="/img/previous-rental-chevron.svg"
					alt=""
					aria-hidden
					className={cn(
						'size-4 shrink-0 origin-center transition-transform duration-200',
						open && 'rotate-180',
					)}
				/>
			</button>
			{open ? (
				<div
					id={panelId}
					className="grid grid-cols-2 gap-4 border-t border-[#F0F0F0] bg-[#fbfdfd] p-4"
				>
					{item.showLandlordFields ? (
						<>
							<FieldBox
								label="Previous Landlord Name"
								value={item.landlordName}
							/>
							{showLandlordPhone(item.landlordPhone) ? (
								<FieldBox
									label="Phone Number"
									value={formatPhoneNumberWithFallback(item.landlordPhone)}
								/>
							) : null}
							{showLandlordEmail(item.landlordEmail) ? (
								<FieldBox label="Email" value={item.landlordEmail} />
							) : null}
							<FieldBox label="Previous Rent (CAD)" value={rent} />
							<FieldBox
								label="Rental Payment Method"
								value={optionLabel(item.paymentMethod)}
							/>
						</>
					) : item.livingSituation ? (
						<FieldBox
							label="Living Situation"
							value={optionLabel(item.livingSituation)}
						/>
					) : null}
					<FieldBox label="Address" value={item.address} />
					{start ? <FieldBox label="Move In Date" value={start} /> : null}
					{end ? <FieldBox label="Move Out Date" value={end} /> : null}
				</div>
			) : null}
		</div>
	)
}
